/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    Grid,
    IconButton,
    Snackbar,
    Typography,
} from '@mui/material';
import { JourneyType } from 'app/constants/Booking/BookingConstants';
import { BookingSummary } from 'components/Bookings/BookingSummary/BookingSummary';
import DisplayPrice from 'components/Common/DisplayValues/DisplayPrice';
import { StepComponentProps } from 'components/RouteWizard/RouteWizard';
import { useWizard } from 'components/RouteWizard/Wizard';
import useBooking from 'hooks/useBooking';
import useClient from 'hooks/useClient';
import type { HandleBookCar } from 'Pages/Client/BookingStatusPage/ReallocateModal/QuotesTitle';
import { useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useSelector } from 'react-redux';
import SelectedVehicles from '../../SelectedVehicles';
import QuoteListing from './QuoteListing';

const convertNumberToString = (n: number) => {
  if (n < 0) return false
  let single_digit = [
    '',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
  ]
  let double_digit = [
    'ten',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
    'seventeen',
    'eighteen',
    'nineteen',
  ]
  let below_hundred = [
    'twenty',
    'thirty',
    'forty',
    'fifty',
    'sixty',
    'seventy',
    'eighty',
    'ninety',
  ]
  if (n === 0) return 'Zero'
  function translate(n: number) {
    let word = ''
    if (n < 10) {
      word = single_digit[n] + ' '
    } else if (n < 20) {
      word = double_digit[n - 10] + ' '
    } else if (n < 100) {
      let rem = translate(n % 10)
      word = below_hundred[(n - (n % 10)) / 10 - 2] + ' ' + rem
    } else if (n < 1000) {
      word =
        single_digit[Math.trunc(n / 100)] + ' hundred ' + translate(n % 100)
    } else if (n < 1000000) {
      word = translate(n / 1000).trim() + ' thousand ' + translate(n % 1000)
    } else if (n < 1000000000) {
      word =
        translate(n / 1000000).trim() + ' million ' + translate(n % 1000000)
    } else {
      word =
        translate(n / 1000000000).trim() +
        ' billion ' +
        translate(n % 1000000000)
    }
    return word
  }
  let result = translate(n)
  return result.trim()
}

interface GSOProps extends StepComponentProps {
  quoteData: {
    prices: any
    reference: any
    inboundReference: any
    isLoading: any
    error: any
  }
}

const pricingBreakdownExcludeVat =
  'Pricing excludes VAT, waiting time, airport drop off/collection fees, parking, tolls, clean air charges and may be subject to congestion zone charges'

const pricingbreakdownIncludeVat =
  'Pricing includes VAT, but may be subject to waiting time, airport drop off/collection fees, parking, tolls, clean air charges and may be subject to congestion zone charges'

const GroupedSupplierOptions = ({
  quoteData,
  onSuccess,
  hideSummary,
}: GSOProps & { hideSummary?: boolean }) => {
  const { data } = useWizard()
  const client = useClient(data.account.clientId)
  const { displayVATPrice } = useClient(data.account.clientId)
  const [spendPolicyLimit, setSpendPolicyLimit] = useState(false)
  const quickQuote = useSelector((state: any) => state.bookings.quickQuote)
  const { prices, reference, inboundReference, isLoading, error } = quoteData
  const [selectMultipleOutBound, setSelectMultipleOutBound] = useState(false)
  const [selectMultipleReturn, setSelectMultipleReturn] = useState(false)
  const [selectedVehicles, setSelectedVehicles] = useState({})
  const [canSelectMoreCars, setCanSelectMoreCars] = useState(true)
  const [showSeatCountWarning, setShowSeatCountWarning] = useState(false)
  const [getTempBookedCars, setTempBookedCars] = useState<any>({
    outBound: [],
    inBound: [],
  })

  const [returnVehicles, setReturnVehicles] = useState(false)

  const [displayQuotePage, setDisplayQuotePage] = useState(false)
  const [preview, setPreview] = useState({
    ...data.preview,
    estimatedPriceCurrency: '',
    estimatedPriceFull: 0,
    selectedVehicle: null,
  })

  const {
    bookingData,
    createBooking,
    isLoading: isLoadingBooking,
    error: bookingError,
    errorMessage,
  } = useBooking()

  useEffect(() => {
    if (bookingError) {
      console.log(bookingError)
      confirmAlert({
        customUI: ({ onClose }) => {
          if (
            errorMessage ===
              'Booking policy job spend limit would exceed booking policy rules, please reach out to an authorised user to make this booking' &&
            client.clientThemeName === 'Veezu'
          ) {
            setSpendPolicyLimit(true)
          }
          return (
            <Dialog
              open={true}
              onClose={onClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle
                id="alert-dialog-title"
                sx={(theme) => ({
                  backgroundColor: '#ee1c00ff',
                  color: theme.palette.info.contrastText,
                  fontSize: 'var(--fs-24, 1.5rem)',
                })}
              >
                Problem with booking
              </DialogTitle>
              <Typography variant="body1" sx={{ paddingY: 3, paddingX: 5 }}>
                {spendPolicyLimit ? (
                  <Typography sx={{ textWrap: 'pretty' }}>
                    Your booking is over the allowed limit. Please call{' '}
                    <a href="tel:+441743294579">01743 294579</a> to arrange your
                    booking.
                  </Typography>
                ) : (
                  errorMessage
                )}
              </Typography>

              <DialogActions>
                <Button onClick={onClose}>Close</Button>
              </DialogActions>
            </Dialog>
          )
        },
      })
    }
  }, [bookingError, createBooking])

  useEffect(() => {
    if (bookingData) {
      onSuccess({
        transport: bookingData,
        prices: prices,
        preview,
      })
    }
  }, [bookingData])

  const handleChangeVehicle = (data: any, type = 'outbound') => {
    setSelectedVehicles({})
    if (type === 'return') {
      setTempBookedCars((prev: any) => ({ ...prev, inBound: [] }))
    } else {
      setTempBookedCars((prev: any) => ({ ...prev, outBound: [] }))
      setDisplayQuotePage(false)
    }
  }

  const getTotalCalculatedPrice = (outbound = [], inbound = []) => {
    let estimatedPriceTotal = {
      total: 0,
      price: 0,
      vat: 0,
      currency: '',
    }
    const getOuboundSelectedVehicle = getTempBookedCars?.outBound?.length
      ? getTempBookedCars?.outBound
      : outbound
    const getInboundSelectedVehicle = getTempBookedCars?.inBound?.length
      ? getTempBookedCars?.inBound
      : inbound

    getOuboundSelectedVehicle?.forEach((val: any) => {
      const vehicleCost = {
        ...val.quotes?.price,
        vat: val.quotes?.price?.vat?.toFixed(2) * val.vehicleData.quantity,
        total: val.quotes?.price?.total?.toFixed(2) * val.vehicleData.quantity,
        price: val.quotes?.price?.price?.toFixed(2) * val.vehicleData.quantity,
      }
      estimatedPriceTotal.currency = vehicleCost.currency
      estimatedPriceTotal.vat += vehicleCost.vat
      estimatedPriceTotal.price += vehicleCost.price
      estimatedPriceTotal.total += vehicleCost.total
    })
    getInboundSelectedVehicle?.forEach((val: any) => {
      const vehicleCost = {
        ...val.quotes?.price,
        vat: val.quotes?.price?.vat?.toFixed(2) * val.vehicleData.quantity,
        total: val.quotes?.price?.total?.toFixed(2) * val.vehicleData.quantity,
        price: val.quotes?.price?.price?.toFixed(2) * val.vehicleData.quantity,
      }
      estimatedPriceTotal.currency = vehicleCost.currency
      estimatedPriceTotal.vat += vehicleCost.vat
      estimatedPriceTotal.price += vehicleCost.price
      estimatedPriceTotal.total += vehicleCost.total
    })

    return estimatedPriceTotal
  }

  const updatePreview = () => {
    setPreview((prevValue: any) => ({
      ...prevValue,
      estimatedPriceFull: getTotalCalculatedPrice(),
    }))
  }

  const handleCreateBooking = (outbound = [] as any) => {
    const getOutBoundCars = getTempBookedCars?.outBound.length
      ? getTempBookedCars?.outBound
      : outbound
    const getInboundCars = getTempBookedCars?.inBound

    setShowSeatCountWarning(false)

    if (!getOutBoundCars?.length) {
      return
    }
    if (
      !getInboundCars?.length &&
      data.plan?.journeyTypeName === JourneyType.RETURN
    ) {
      return
    }
    const outBoundSeats = getOutBoundCars?.reduce((acc: any, curr: any) => {
      return (acc += curr?.seats) * curr?.vehicleData?.quantity
    }, 0)
    const inBoundSeats = getInboundCars?.reduce((acc: any, curr: any) => {
      return (acc += curr?.seats) * curr?.vehicleData?.quantity
    }, 0)

    if (outBoundSeats < data?.account?.numberOfPassengers) {
      return setShowSeatCountWarning(true)
    }
    if (
      getInboundCars?.length &&
      data.plan?.journeyTypeName === JourneyType.RETURN
    ) {
      if (inBoundSeats < data?.account?.numberOfPassengers) {
        return setShowSeatCountWarning(true)
      }
    }

    const bookingData: any = {
      outbound: {
        quoteReference: reference,
        vehicles: getOutBoundCars?.map((v: any) => ({
          supplierQuoteReference: v.vehicleData.supplierQuoteReference,
          quantity: v.vehicleData.quantity,
        })),
      },
    }

    if (
      data.plan?.journeyTypeName === JourneyType.RETURN &&
      getInboundCars?.length &&
      inboundReference
    ) {
      bookingData.inbound = {
        quoteReference: inboundReference,
        vehicles: getInboundCars
          ?.filter((v: any) => v.vehicleData.inboundSupplierQuoteReference)
          ?.map((v: any) => ({
            supplierQuoteReference: v.vehicleData.inboundSupplierQuoteReference,
            quantity: v.vehicleData.quantity,
          })),
      }
    }
    const policySet = new Set<string>()
    const pushPolicy = (value: any) => {
      if (typeof value !== 'string') return
      const trimmed = value.trim()
      if (trimmed) policySet.add(trimmed)
    }
    for (const car of [...(getOutBoundCars || []), ...(getInboundCars || [])]) {
      pushPolicy(car?.quotes?.cancellationPolicy)
      pushPolicy(car?.quotes?.inboundCancellationPolicy)
      pushPolicy(car?.cancellationPolicy)
      pushPolicy(car?.vehicleDetails?.cancellationPolicy)
      pushPolicy(car?.quotes?.vehicleDetails?.cancellationPolicy)
    }
    const cancellationPolicies = Array.from(policySet)

    if (quickQuote || hideSummary) {
      onSuccess({
        transport: bookingData,
        prices: prices,
        preview: {
          ...preview,
          seats: inBoundSeats + outBoundSeats,
          estimatedPriceFull: getTotalCalculatedPrice(
            getOutBoundCars,
            getInboundCars,
          ),
          cancellationPolicies,
        },
      })
    } else {
      createBooking(bookingData)
    }
  }

  const handleBookNow = ({ data: details, vehicleDetails }: HandleBookCar) => {
    const wrapper = document.getElementById('jyrney-booking-wrapper')
    if (wrapper) {
      wrapper.scrollTo(0, 0)
    }
    setTempBookedCars((prev: any) => ({
      ...prev,
      outBound: [{ ...vehicleDetails, vehicleData: details }],
    }))
    if (data?.plan?.journeyTypeName === JourneyType.RETURN) {
      setDisplayQuotePage(true)
    } else {
      handleCreateBooking([{ ...vehicleDetails, vehicleData: details }])
    }
    updatePreview()
  }

  const handleReturnTripVehicle = ({ data, vehicleDetails }: HandleBookCar) => {
    setTempBookedCars((prev: any) => ({
      ...prev,
      inBound: [{ ...vehicleDetails, vehicleData: data }],
    }))
    updatePreview()
  }

  const getSelectedMultipleData = () => {
    const selectNewConfirmVehicles = Object.entries(selectedVehicles).map(
      (val: any) => {
        const value = val[1]
        const counterValue = value.counterValue
        const newData = {
          ...counterValue,
          vehicleData: {
            supplierQuoteReference:
              counterValue.quotes?.supplierQuoteReference || '',
            seats: counterValue.seats,
            estimatedPriceCurrency:
              counterValue.quotes?.estimatedPriceCurrency || '',
            price: counterValue.quotes?.price?.price || 0,
            inboundSupplierQuoteReference:
              counterValue.quotes?.inboundSupplierQuoteReference || '',
            quantity: value.quantity,
          },
        }
        delete newData.counterValue
        return { ...newData }
      },
    )
    const filterNoQuantity = selectNewConfirmVehicles.filter(
      (v) => v.vehicleData.quantity > 0,
    )

    return filterNoQuantity
  }

  const confirmOutboundDetails = () => {
    setDisplayQuotePage(true)
    setSelectedVehicles({})
    setTempBookedCars((prev: any) => ({
      ...prev,
      outBound: getSelectedMultipleData(),
    }))
    updatePreview()
  }

  const confirmInboundDetails = () => {
    setTempBookedCars((prev: any) => ({
      ...prev,
      inBound: getSelectedMultipleData(),
    }))
    updatePreview()
  }

  const handleMultipleVehicleChange = (
    key: number,
    type: 'inBound' | 'outBound',
    operator: 'positive' | 'negative',
  ) => {
    let newCount = 0
    const addTempJsonBookedCars = JSON.parse(JSON.stringify(getTempBookedCars))
    const getAllVehicleQuantity = addTempJsonBookedCars[type].reduce(
      (acc: any, curr: any) => {
        const currentData = curr.vehicleData.quantity
        acc += currentData
        return acc
      },
      0,
    )
    const previousQuantity =
      addTempJsonBookedCars[type][key].vehicleData.quantity
    if (
      operator === 'positive' &&
      data?.account?.numberOfPassengers <= getAllVehicleQuantity
    ) {
      return
    }
    newCount =
      operator === 'negative' ? previousQuantity - 1 : previousQuantity + 1
    addTempJsonBookedCars[type][key].vehicleData.quantity = newCount
    setTempBookedCars((curr: any) => ({
      ...curr,
      [type]: addTempJsonBookedCars[type],
    }))
  }

  useEffect(() => {
    let estimatedPriceTotal = {
      total: 0,
      price: 0,
      vat: 0,
      currency: '',
    }
    let estimatedPriceCurrency = ''
    let totalOutboundCount = 0
    let totalInboundCount = 0
    const getOuboundSelectedVehicle = getTempBookedCars.outBound
    const getInboundSelectedVehicle = getTempBookedCars.inBound

    getOuboundSelectedVehicle?.forEach((val: any) => {
      const vehicleCost = {
        ...val.quotes?.price,
        vat: val.quotes?.price?.vat?.toFixed(2) * val.vehicleData.quantity,
        total: val.quotes?.price?.total?.toFixed(2) * val.vehicleData.quantity,
        price: val.quotes?.price?.price?.toFixed(2) * val.vehicleData.quantity,
      }
      estimatedPriceTotal.currency = vehicleCost.currency
      estimatedPriceTotal.vat += vehicleCost.vat
      estimatedPriceTotal.price += vehicleCost.price
      estimatedPriceTotal.total += vehicleCost.total
      totalOutboundCount += val.vehicleData?.quantity
      estimatedPriceCurrency = val.vehicleData?.estimatedPriceCurrency
    })
    getInboundSelectedVehicle?.forEach((val: any) => {
      const vehicleCost = {
        ...val.quotes?.price,
        vat: val.quotes?.price?.vat?.toFixed(2) * val.vehicleData.quantity,
        total: val.quotes?.price?.total?.toFixed(2) * val.vehicleData.quantity,
        price: val.quotes?.price?.price?.toFixed(2) * val.vehicleData.quantity,
      }
      estimatedPriceTotal.currency = vehicleCost.currency
      estimatedPriceTotal.vat += vehicleCost.vat
      estimatedPriceTotal.price += vehicleCost.price
      estimatedPriceTotal.total += vehicleCost.total
      totalInboundCount += val.vehicleData?.quantity
      estimatedPriceCurrency = val.vehicleData?.estimatedPriceCurrency
    })

    setCanSelectMoreCars(
      data.account?.numberOfPassengers > totalOutboundCount ||
        data.account?.numberOfPassengers > totalInboundCount,
    )
    setPreview((prevValue: any) => ({
      ...prevValue,
      estimatedPriceFull: estimatedPriceTotal,
      estimatedPriceCurrency,
    }))
  }, [getTempBookedCars])

  useEffect(() => {
    setSelectedVehicles({})
    setTempBookedCars((prev: any) => ({
      ...prev,
      outBound: [],
    }))
  }, [selectMultipleOutBound])

  useEffect(() => {
    setSelectedVehicles({})
    setTempBookedCars((prev: any) => ({
      ...prev,
      inBound: [],
    }))
  }, [selectMultipleReturn])

  const selectedNumberVehicle = Object.entries(selectedVehicles).reduce(
    (acc: any, curr: any) => {
      const currentData = curr[1]
      acc += currentData?.quantity
      return acc
    },
    0,
  )

  const renderInboundPrice = (prices: any) =>
    Object.entries(prices).map(([key, group]: any) => (
      <QuoteListing
        key={key}
        vehicleDetails={group?.[0]?.vehicleDetails}
        quotes={group}
        isImmediateJourney={data.plan?.isImmediateJourney}
        returnVehicles={returnVehicles}
        handleBookNow={handleReturnTripVehicle}
        selectMultiple={selectMultipleReturn}
        selectedVehicles={selectedVehicles}
        setSelectedVehicles={setSelectedVehicles}
        canSelectMoreCars={canSelectMoreCars}
        client={client}
      />
    ))

  const renderOuboundPrice = (prices: any) =>
    Object.entries(prices).map(([key, group]: any) => (
      <QuoteListing
        key={key}
        vehicleDetails={group?.[0]?.vehicleDetails}
        quotes={group}
        isImmediateJourney={data.plan?.isImmediateJourney}
        returnVehicles={returnVehicles}
        handleBookNow={handleBookNow}
        selectMultiple={selectMultipleOutBound}
        selectedVehicles={selectedVehicles}
        setSelectedVehicles={setSelectedVehicles}
        canSelectMoreCars={canSelectMoreCars}
        client={client}
      />
    ))

  if (displayQuotePage) {
    const totalPrice = getTotalCalculatedPrice()
    return (
      <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
        <Grid item xs={hideSummary ? 12 : 6}>
          <Box
            component="div"
            sx={{
              float: 'right',
              width: '100%',
            }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '24px',
              }}
            >
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  alignItems: displayQuotePage ? 'center' : 'start',
                }}
              >
                <Typography
                  component="p"
                  sx={() => ({
                    color: '#0A2435',
                    fontWeight: '700',
                    fontSize: '19px',
                    lineHeight: '21px',
                  })}
                >
                  OUTBOUND
                </Typography>
                {selectMultipleOutBound && (
                  <Button
                    sx={{
                      textTransform: 'uppercase',
                    }}
                    onClick={() => handleChangeVehicle({})}
                  >
                    Change
                  </Button>
                )}
              </Box>
            </Box>
            <Box
              component="div"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
              }}
            >
              {getTempBookedCars.outBound?.map((v: any, key: number) => (
                <SelectedVehicles
                  key={key}
                  selectMultiple={selectMultipleOutBound}
                  keyValue={key}
                  type="outBound"
                  data={v}
                  handleChangeVehicle={handleChangeVehicle}
                  handleMultipleVehicleChange={handleMultipleVehicleChange}
                />
              ))}
            </Box>
            {data?.plan?.journeyTypeName === JourneyType.RETURN &&
              !returnVehicles && (
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    marginTop: '48px',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    sx={{
                      textTransform: 'uppercase',
                    }}
                    onClick={() => setReturnVehicles(true)}
                  >
                    CHOOSE RETURN VEHICLE
                  </Button>
                </Box>
              )}
            {returnVehicles && (
              <>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '32px',
                  }}
                >
                  <Typography
                    component="p"
                    sx={() => ({
                      color: '#0A2435',
                      fontWeight: '700',
                      fontSize: '19px',
                      lineHeight: '21px',
                    })}
                  >
                    RETURN TRIP
                  </Typography>
                  {data?.plan?.journeyTypeName === JourneyType.RETURN &&
                  selectMultipleReturn &&
                  getTempBookedCars?.inBound?.length ? (
                    <Button
                      sx={{
                        textTransform: 'uppercase',
                      }}
                      onClick={() => handleChangeVehicle({}, 'return')}
                    >
                      Change
                    </Button>
                  ) : (
                    <></>
                  )}
                </Box>
                {isLoading && getTempBookedCars?.inBound?.length === 0 && (
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      marginTop: '24px',
                    }}
                  >
                    <Typography align="center" paragraph>
                      Getting your quotes, please wait.
                    </Typography>
                    <CircularProgress size={12} />
                  </Box>
                )}
                {!isLoading && getTempBookedCars?.inBound?.length === 0 && (
                  <Typography
                    component="p"
                    sx={() => ({
                      marginTop: '4px',
                      color: '#414042',
                      fontWeight: '300',
                      fontSize: '17px',
                      lineHeight: '21px',
                    })}
                  >
                    Select a car for your ride back to
                    {` ${data?.preview?.pickup?.formattedAddress}` || ''}
                  </Typography>
                )}
                {!isLoading &&
                getTempBookedCars?.inBound?.length === 0 &&
                data?.account?.numberOfPassengers > 1 ? (
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      marginBottom: '24px',
                      marginTop: '24px',
                    }}
                  >
                    <Box
                      component="div"
                      sx={{
                        background: selectMultipleReturn ? '#F3F3F3' : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '12px 32px',
                        width: '50%',
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectMultipleReturn(false)}
                    >
                      <Typography
                        component="p"
                        sx={() => ({
                          color: selectMultipleReturn ? '#9CA6AD' : '#21384C',
                          fontWeight: selectMultipleReturn ? '300' : '500',
                          fontSize: '16px',
                          lineHeight: '24px',
                        })}
                      >
                        Single Car
                      </Typography>
                    </Box>
                    <Box
                      component="div"
                      sx={{
                        background: selectMultipleReturn ? '#fff' : '#F3F3F3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '12px 32px',
                        width: '50%',
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectMultipleReturn(true)}
                    >
                      <Typography
                        component="p"
                        sx={() => ({
                          color: selectMultipleOutBound ? '#21384C' : '#9CA6AD',
                          fontWeight: selectMultipleOutBound ? '500' : '300',
                          fontSize: '16px',
                          lineHeight: '24px',
                        })}
                      >
                        Multiple Cars
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <></>
                )}
                {selectMultipleReturn &&
                  selectedNumberVehicle > 0 &&
                  getTempBookedCars?.inBound?.length === 0 && (
                    <Box
                      component="div"
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                        alignItems: 'center',
                        marginBottom: '24px',
                      }}
                    >
                      <Box
                        component="div"
                        sx={{
                          display: 'flex',
                          padding: '12px',
                          border: '1px solid #056A46',
                          background: '#EFFFEB',
                          color: '#056A46',
                          width: 'calc(100% - 102px)',
                          borderRadius: '4px',
                        }}
                      >
                        {`You have selected ${convertNumberToString(
                          selectedNumberVehicle,
                        )}
                          (${selectedNumberVehicle}) car${
                          selectedNumberVehicle > 1 ? 's' : ''
                        }`}
                      </Box>
                      <Button
                        sx={{
                          textTransform: 'uppercase',
                          width: '94px',
                          height: '48px',
                        }}
                        onClick={confirmInboundDetails}
                      >
                        Confirm
                      </Button>
                    </Box>
                  )}
                <Box
                  component="div"
                  sx={{
                    marginTop: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap:
                      getTempBookedCars?.inBound?.length === 0 ? '0' : '24px',
                  }}
                >
                  {getTempBookedCars?.inBound?.length === 0 ? (
                    <>
                      {!isLoading &&
                        !error &&
                        prices &&
                        renderInboundPrice(prices)}
                    </>
                  ) : (
                    <>
                      {getTempBookedCars.inBound?.map((v: any, key: number) => (
                        <SelectedVehicles
                          key={key}
                          data={v}
                          selectMultiple={selectMultipleReturn}
                          keyValue={key}
                          type="inBound"
                          handleMultipleVehicleChange={
                            handleMultipleVehicleChange
                          }
                          handleChangeVehicle={(data) =>
                            handleChangeVehicle(data, 'return')
                          }
                        />
                      ))}
                    </>
                  )}
                </Box>
              </>
            )}
            {data?.plan?.journeyTypeName === JourneyType.ONE_WAY ||
            (getTempBookedCars?.outBound?.length &&
              getTempBookedCars?.inBound?.length) ? (
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '48px',
                }}
              >
                <Typography
                  component="p"
                  sx={() => ({
                    color: '#0A2435',
                    fontWeight: '700',
                    fontSize: '19px',
                    lineHeight: '21px',
                  })}
                >
                  TOTAL PRICE
                </Typography>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  }}
                >
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                    }}
                  >
                    <Typography
                      component="p"
                      sx={() => ({
                        color: '#414042',
                        fontWeight: '400',
                        fontSize: '19px',
                        lineHeight: '21px',
                        marginRight: '4px',
                      })}
                    >
                      ESTIMATED
                    </Typography>
                    <Typography
                      component="p"
                      sx={() => ({
                        color: '#414042',
                        fontWeight: '600',
                        fontSize: '19px',
                        lineHeight: '21px',
                      })}
                    >
                      {DisplayPrice({
                        VATTotalPrice: totalPrice,
                        displayVATPrice: displayVATPrice,
                      })}
                    </Typography>
                  </Box>
                  <Button
                    disabled={isLoadingBooking}
                    sx={{
                      padding: '16px',
                      marginTop: '48px',
                      textTransform: 'uppercase',
                      width: 'max-content',
                      '&:disabled': {
                        color: '#fff',
                      },
                    }}
                    onClick={() => handleCreateBooking()}
                  >
                    BOOK CARS
                  </Button>
                </Box>
              </Box>
            ) : (
              <></>
            )}
          </Box>
        </Grid>
        {!hideSummary && (
          <Grid item xs={6} sx={{ alignSelf: 'flex-start' }}>
            <Box
              component="div"
              sx={{
                position: 'sticky',
                top: '20px',
              }}
            >
              <BookingSummary
                bookingPreview={preview}
                getTempBookedCars={getTempBookedCars}
                isLoadingBooking={
                  isLoadingBooking ||
                  (data.plan?.journeyTypeName === JourneyType.RETURN &&
                    !(
                      getTempBookedCars?.outBound?.length &&
                      getTempBookedCars?.inBound?.length
                    ))
                }
                onSubmit={handleCreateBooking}
                onPrecheckSubmit={() => {}}
                onRouteInfoChange={() => {}}
              />
            </Box>
          </Grid>
        )}
        <Snackbar
          open={showSeatCountWarning}
          autoHideDuration={6000}
          onClose={() => setShowSeatCountWarning(false)}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              sx={{ p: 0.5 }}
              onClick={() => setShowSeatCountWarning(false)}
            >
              <CloseIcon />
            </IconButton>
          }
        >
          <Alert severity="error">
            You have not selected enough cars for your journey.
          </Alert>
        </Snackbar>
      </Grid>
    )
  }

  if (!isLoading && !error && !prices) {
    return (
      <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
        <Grid item xs={7}>
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography align="center" paragraph>
              Sorry there are no quotes available for your journey.
            </Typography>
          </Box>
        </Grid>
        {!hideSummary && (
          <Grid item xs={5} sx={{ alignSelf: 'flex-start' }}>
            <Box
              component="div"
              sx={{
                position: 'sticky',
                top: '20px',
              }}
            >
              <BookingSummary
                bookingPreview={preview}
                getTempBookedCars={getTempBookedCars}
                isLoadingBooking={
                  isLoadingBooking ||
                  (data.plan?.journeyTypeName === JourneyType.RETURN &&
                    !(
                      getTempBookedCars?.outBound?.length &&
                      getTempBookedCars?.inBound?.length
                    ))
                }
                onSubmit={handleCreateBooking}
                onPrecheckSubmit={() => {}}
                onRouteInfoChange={() => {}}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    )
  }

  return (
    <>
      <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
        <Grid item xs={hideSummary ? 12 : 6}>
          <Box
            component="div"
            sx={{
              float: 'right',
              width: '100%',
            }}
          >
            {data?.account?.numberOfPassengers > 1 ? (
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  marginBottom: '24px',
                }}
              >
                <Box
                  component="div"
                  sx={{
                    background: selectMultipleOutBound ? '#F3F3F3' : '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px 32px',
                    width: '50%',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectMultipleOutBound(false)}
                >
                  <Typography
                    component="p"
                    sx={() => ({
                      color: selectMultipleOutBound ? '#9CA6AD' : '#21384C',
                      fontWeight: selectMultipleOutBound ? '300' : '500',
                      fontSize: '16px',
                      lineHeight: '24px',
                    })}
                  >
                    Single Car
                  </Typography>
                </Box>
                <Box
                  component="div"
                  sx={{
                    background: selectMultipleOutBound ? '#fff' : '#F3F3F3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px 32px',
                    width: '50%',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectMultipleOutBound(true)}
                >
                  <Typography
                    component="p"
                    sx={() => ({
                      color: selectMultipleOutBound ? '#21384C' : '#9CA6AD',
                      fontWeight: selectMultipleOutBound ? '500' : '300',
                      fontSize: '16px',
                      lineHeight: '24px',
                    })}
                  >
                    Multiple Cars
                  </Typography>
                </Box>
              </Box>
            ) : (
              <></>
            )}
            {selectMultipleOutBound && selectedNumberVehicle > 0 && (
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '8px',
                  alignItems: 'center',
                  marginBottom: '24px',
                }}
              >
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    padding: '12px',
                    border: '1px solid #056A46',
                    background: '#EFFFEB',
                    color: '#056A46',
                    width: 'calc(100% - 102px)',
                    borderRadius: '4px',
                  }}
                >
                  {`You have selected ${convertNumberToString(
                    selectedNumberVehicle,
                  )}
                    (${selectedNumberVehicle}) car${
                    selectedNumberVehicle > 1 ? 's' : ''
                  }`}
                </Box>
                <Button
                  sx={{
                    textTransform: 'uppercase',
                    width: '94px',
                    height: '48px',
                  }}
                  onClick={confirmOutboundDetails}
                >
                  Confirm
                </Button>
              </Box>
            )}
            {!isLoading && !error && prices && renderOuboundPrice(prices)}
          </Box>
        </Grid>
        {!hideSummary && (
          <Grid item xs={6} sx={{ alignSelf: 'flex-start' }}>
            <Box
              component="div"
              sx={{
                position: 'sticky',
                top: '20px',
              }}
            >
              <BookingSummary
                bookingPreview={preview}
                getTempBookedCars={getTempBookedCars}
                isLoadingBooking={
                  isLoadingBooking ||
                  (data.plan?.journeyTypeName === JourneyType.RETURN &&
                    !(
                      getTempBookedCars?.outBound?.length &&
                      getTempBookedCars?.inBound?.length
                    ))
                }
                onSubmit={handleCreateBooking}
              />
              <Box
                component="div"
                sx={{
                  fontSize: '12px',
                  fontStyle: 'italic',
                  color: '#414042',
                  minHeight: '70px',
                  margin: 'auto',
                  display: 'flex',
                  width: '50%',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '5px',
                  textAlign: 'center',
                }}
              >
                {displayVATPrice
                  ? pricingbreakdownIncludeVat
                  : pricingBreakdownExcludeVat}
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
      <Snackbar
        open={showSeatCountWarning}
        autoHideDuration={6000}
        onClose={() => setShowSeatCountWarning(false)}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.5 }}
            onClick={() => setShowSeatCountWarning(false)}
          >
            <CloseIcon />
          </IconButton>
        }
      >
        <Alert severity="error">
          You have not selected enough cars for your journey.
        </Alert>
      </Snackbar>
    </>
  )
}

export default GroupedSupplierOptions
